import axiosInstance from '../clients/axiosClient';
import {
  API_V1_PREFIX,
  MRI_CONDITIONS_API_PREFIX,
  RADIOLOGIST_BODY_SYSTEMS_API_PREFIX,
  RADIOLOGY_ORGANS_API_PREFIX,
} from '../utils/constants';

export type MriConditionGender = 'male' | 'female' | 'both';

export interface NamedEntity {
  id: number;
  name: string;
  description?: string | null;
  image?: string | null;
  image_url?: string | null;
}

export interface MriConditionBodySystem {
  id: number;
  title: string;
}

export interface MriCondition {
  id: number;
  radiologist_body_system_id: number;
  body_systems: MriConditionBodySystem[];
  radiology_organ_id: number;
  radiologist_body_system_name: string;
  radiology_organ_name: string;
  gender: MriConditionGender;
  condition_description: string;
  enabled: boolean;
  guidelines?: string | null;
}

export interface ListMriConditionsResponse {
  items: MriCondition[];
  total_count: number;
  limit: number;
  offset: number;
  status: string;
}

export interface CreateMriConditionRequest {
  radiologist_body_system_id: number;
  body_system_ids: number[];
  radiology_organ_id: number;
  gender: MriConditionGender;
  condition_description: string;
  enabled?: boolean;
  guidelines?: string | null;
}

export interface UpdateMriConditionRequest extends Partial<CreateMriConditionRequest> {
  id: number;
}

export interface ListNamedEntitiesResponse {
  items: NamedEntity[];
  total_count: number;
  limit: number;
  offset: number;
  status: string;
}

export interface CreateNamedEntityRequest {
  name: string;
  description?: string | null;
  image?: string | null;
}

export interface UpdateNamedEntityRequest extends Partial<CreateNamedEntityRequest> {
  id: number;
}

// MRI Conditions
export const getMriConditionsList = async (
  limit = 200,
  offset = 0,
  filters?: {
    radiologist_body_system_id?: number;
    body_system_id?: number;
    radiology_organ_id?: number;
    enabled?: boolean;
  }
): Promise<ListMriConditionsResponse> => {
  const { data } = await axiosInstance.get(`${API_V1_PREFIX}${MRI_CONDITIONS_API_PREFIX}list`, {
    params: { limit, offset, ...filters },
  });
  return data;
};

export const createMriCondition = async (payload: CreateMriConditionRequest) => {
  const { data } = await axiosInstance.post(`${API_V1_PREFIX}${MRI_CONDITIONS_API_PREFIX}`, payload);
  return data;
};

export const updateMriCondition = async (payload: UpdateMriConditionRequest) => {
  const { data } = await axiosInstance.patch(`${API_V1_PREFIX}${MRI_CONDITIONS_API_PREFIX}`, payload);
  return data;
};

export const deleteMriCondition = async (entityId: number) => {
  await axiosInstance.delete(`${API_V1_PREFIX}${MRI_CONDITIONS_API_PREFIX}${entityId}`);
};

// Radiologist Body Systems
export const getRadiologistBodySystemsList = async (
  limit = 200,
  offset = 0,
  name?: string
): Promise<ListNamedEntitiesResponse> => {
  const { data } = await axiosInstance.get(
    `${API_V1_PREFIX}${RADIOLOGIST_BODY_SYSTEMS_API_PREFIX}list`,
    { params: { limit, offset, name: name || undefined } }
  );
  return data;
};

export const createRadiologistBodySystem = async (payload: CreateNamedEntityRequest) => {
  const { data } = await axiosInstance.post(
    `${API_V1_PREFIX}${RADIOLOGIST_BODY_SYSTEMS_API_PREFIX}`,
    payload
  );
  return data;
};

export const updateRadiologistBodySystem = async (payload: UpdateNamedEntityRequest) => {
  const { data } = await axiosInstance.patch(
    `${API_V1_PREFIX}${RADIOLOGIST_BODY_SYSTEMS_API_PREFIX}`,
    payload
  );
  return data;
};

export const deleteRadiologistBodySystem = async (entityId: number) => {
  await axiosInstance.delete(`${API_V1_PREFIX}${RADIOLOGIST_BODY_SYSTEMS_API_PREFIX}${entityId}`);
};

// Radiology Organs
export const getRadiologyOrgansList = async (
  limit = 200,
  offset = 0,
  name?: string
): Promise<ListNamedEntitiesResponse> => {
  const { data } = await axiosInstance.get(`${API_V1_PREFIX}${RADIOLOGY_ORGANS_API_PREFIX}list`, {
    params: { limit, offset, name: name || undefined },
  });
  return data;
};

export const createRadiologyOrgan = async (payload: CreateNamedEntityRequest) => {
  const { data } = await axiosInstance.post(`${API_V1_PREFIX}${RADIOLOGY_ORGANS_API_PREFIX}`, payload);
  return data;
};

export const updateRadiologyOrgan = async (payload: UpdateNamedEntityRequest) => {
  const { data } = await axiosInstance.patch(`${API_V1_PREFIX}${RADIOLOGY_ORGANS_API_PREFIX}`, payload);
  return data;
};

export const deleteRadiologyOrgan = async (entityId: number) => {
  await axiosInstance.delete(`${API_V1_PREFIX}${RADIOLOGY_ORGANS_API_PREFIX}${entityId}`);
};
